import React, { useState } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Hug, maxWidths, Text } from '@cash-design-system/react'
import { Header, PaddingInset, spacing, Stack } from '@cash-design-system/react'
import {
  InstrumentSelectionBlocker,
  InstrumentSelectionBlockerInstrumentOption,
} from '@cash-web/protos/squareup/franklin/blockers/instrument_selection_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSelectInstrumentV2 } from '@cash-web/shared-data-access-api/react-query/useMutationSelectInstrumentV2'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { InstrumentSelectViewOptions } from '@squareup/customer-data-events-web'
import { InstrumentSelectTapOption } from '@squareup/customer-data-events-web'

import { BaseBlocker, SubmitButton } from '../BaseBlocker/BaseBlocker'
import { DetailRowElement } from '../FormBlocker/elements/DetailRowElement/DetailRowElement'
import { InstrumentOption } from './InstrumentOption/InstrumentOption'

export type BlockerInstrumentSelectionProps = {
  blocker: InstrumentSelectionBlocker
}

type BlockerInstrumentSelectionFormFileds = {
  encoded_selected_option?: string
}

function alertOnUnimplementedFeatures(blocker: InstrumentSelectionBlocker) {
  if (blocker.show_options_action) {
    logger.error(new Error('BlockerInstrumentSelection: show_options_action is unimplemented'))
  }

  if (blocker.show_dialog_action) {
    logger.error(new Error('BlockerInstrumentSelection: show_dialog_action is unimplemented'))
  }

  blocker.options?.forEach(option => {
    const detailText = option.enabled_state?.select_instrument_action?.detail?.text
    if (detailText) {
      logger.error(new Error('BlockerInstrumentSelection: detail.text is unimplemented'))
    }
  })
}

// Return value example: 'SUBMIT,SELECT,SELECT'
function actionsForLogging(options?: InstrumentSelectionBlockerInstrumentOption[]) {
  const actions = options?.map(actionForLogging)
  return actions ? actions.join(',') : undefined
}

function actionForLogging(option: InstrumentSelectionBlockerInstrumentOption) {
  if (option.disabled_state) {
    return 'DISABLED'
  } else if (option.enabled_state?.submit_instrument_action) {
    return 'SUBMIT'
  } else if (option.enabled_state?.select_instrument_action) {
    return 'SELECT'
  } else if (option.enabled_state?.show_dialog_action) {
    return 'SHOW_DIALOG'
  }
  return undefined
}

export const BlockerInstrumentSelection: React.FC<BlockerInstrumentSelectionProps> = ({ blocker }) => {
  if (!blocker.options || blocker.options.length < 1) {
    throw new Error('BlockerInstrumentSelection: no options provided')
  }

  const preSelectedOption = blocker.options[blocker.preselected_option_index ?? 0]
  const form = useForm<BlockerInstrumentSelectionFormFileds>({
    mode: 'onSubmit',
    defaultValues: { encoded_selected_option: preSelectedOption?.enabled_state?.encoded_selected_option },
  })

  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const flow = useFlowContext()

  const { handleBlockerResponse } = useBlockerResponseHandler()
  const { mutateAsync: selectInstrumentV2 } = useMutationSelectInstrumentV2()

  const [blockerState, setBlockerState] = useState<'default' | 'choosing-transfer-from'>('default')

  alertOnUnimplementedFeatures(blocker)

  const selectedOption = blocker.options.find(
    option => option.enabled_state?.encoded_selected_option === form.getValues().encoded_selected_option
  )
  const detailRows = selectedOption?.enabled_state?.select_instrument_action?.detail_list?.detail_rows
  const optionsHeader = blocker.selected_instrument_label

  const submitBtn = <SubmitButton text={blocker.submit_button_title} key="instrument-selection-submit" />

  const handleSubmit = async ({ encoded_selected_option }: BlockerInstrumentSelectionFormFileds) => {
    await handleBlockerResponse({
      promisedBlockerUpdate: selectInstrumentV2({ encoded_selected_option }),
      defaultError: t('Sorry, an error occurred. Please try again.', {
        context: 'this is the default error message when selecting an instrument errors',
      }),
      isResponseSuccess: response => {
        const blockerDescriptors = response.response_context?.scenario_plan?.blocker_descriptors
        return !!blockerDescriptors?.length && blockerDescriptors?.length > 0
      },
    })
  }

  function handleOptionsClick() {
    logCdfEvent(
      InstrumentSelectViewOptions({
        clientScenario: flow.active?.clientScenario,
        flowToken: flow.active?.flowToken,
        actions: actionsForLogging(blocker.options),
      })
    )

    setBlockerState('choosing-transfer-from')
  }

  function handleChooseFromOption(option: InstrumentSelectionBlockerInstrumentOption) {
    logCdfEvent(
      InstrumentSelectTapOption({
        clientScenario: flow.active?.clientScenario,
        flowToken: flow.active?.flowToken,
        action: actionForLogging(option),
      })
    )

    form.setValue('encoded_selected_option', option.enabled_state?.encoded_selected_option)
    setBlockerState('default')
  }

  return (
    <>
      {blockerState === 'default' && (
        <BaseBlocker form={form} mainText={blocker.header_text} buttons={[submitBtn]} onSubmit={handleSubmit}>
          <Stack space={spacing.xxl}>
            {detailRows && detailRows.length > 0 && (
              <Stack space={spacing.xs}>
                {detailRows.map(row => (
                  <DetailRowElement key={row.value} title={row.label} detail_text={row.value} />
                ))}
              </Stack>
            )}
            <PaddingInset>
              <Stack space={spacing.m}>
                {optionsHeader && <Header variant="labelMedium">{optionsHeader}</Header>}
                {selectedOption && (
                  <InstrumentOption
                    option={selectedOption}
                    variant="selection"
                    onClick={blocker.options && blocker.options.length > 1 ? handleOptionsClick : undefined}
                  />
                )}
              </Stack>
            </PaddingInset>
          </Stack>
        </BaseBlocker>
      )}
      {blockerState === 'choosing-transfer-from' && (
        <Hug maxWidth={maxWidths.content} fullWidth={true}>
          <Stack space={spacing.xxl}>
            <Text typography="pageTitle" color="textStandard">
              {blocker.list_header_title}
            </Text>
            <Stack space={spacing.xl}>
              {blocker.options?.map(option => {
                return (
                  <InstrumentOption
                    key={option.list_title}
                    option={option}
                    variant="list"
                    onClick={() => handleChooseFromOption(option)}
                  />
                )
              })}
            </Stack>
          </Stack>
        </Hug>
      )}
    </>
  )
}
